/**
 * When BlogGrid has a section-level `cta`, it should render inside the matching
 * Blog summary card (instead of below the grid) if the link targets that article.
 */

/**
 * @param {string|undefined|null} raw
 * @returns {string}
 */
const pathnameNormalized = (raw) => {
  if (raw == null || raw === '') return '';

  let s = String(raw).trim();
  try {
    if (/^https?:\/\//i.test(s)) {
      s = new URL(s).pathname;
    } else {
      s = s.startsWith('/') ? s : `/${s}`;
    }
  } catch {
    return '';
  }

  const decoded = (() => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  })();

  let out = decoded.replace(/\\/g, '/');
  while (out.length > 1 && out.endsWith('/')) {
    out = out.slice(0, -1);
  }

  return out.toLowerCase();
};

/**
 * @param {Pick<Contentful.Link, 'url'> | null | undefined} cta
 * @param {Contentful.Blog | null | undefined} blog
 * @param {string} [slugOverride] Passed from BlogGrid (`pageSlug`), same as BlogSummary slug prop priority
 */
export function gridSectionCtaMatchesBlog(cta, blog, slugOverride) {
  const ctaPath = pathnameNormalized(cta?.url ?? '');
  if (!ctaPath || blog?.__typename !== 'Blog') return false;

  const slugPath = pathnameNormalized(slugOverride);
  const blogPaths = [
    slugPath,
    pathnameNormalized(blog.url ?? ''),
    pathnameNormalized(blog.pageSlug),
    pathnameNormalized(blog.slug),
  ].filter(Boolean);

  return blogPaths.some((p) => p === ctaPath);
}

const lastPathSegment = (raw) => {
  const p = pathnameNormalized(raw);

  if (!p) return '';

  const parts = p.split('/').filter(Boolean);

  return (parts[parts.length - 1] ?? '').toLowerCase();
};

/**
 * Which Blog tile should host the grid section CTA inside {@link BlogSummary}.
 * 1) Full pathname match vs CTA url
 * 2) Last URL segment match (handles locale / base path drift)
 * 3) First item in collection (hero cell in twoColLeft / Style1 featured)
 *
 * @param {Pick<Contentful.Link, 'url'> | null | undefined} cta
 * @param {Array<Contentful.Blog|object>} [blogSummaries]
 * @returns {string|null}
 */
export function resolveInlinedBlogGridCtaBlogSysId(cta, blogSummaries = []) {
  if (!cta?.url) return null;

  const byFullPath = blogSummaries.find((b) =>
    gridSectionCtaMatchesBlog(cta, b, b.pageSlug),
  );
  if (byFullPath?.sys?.id) return byFullPath.sys.id;

  const ctaSlug = lastPathSegment(cta.url);
  if (ctaSlug) {
    const bySlug = blogSummaries.find((b) => {
      if (b?.__typename !== 'Blog') return false;
      const urls = [b.pageSlug, b.slug, b.url].filter(Boolean);

      return urls.some((u) => lastPathSegment(u) === ctaSlug);
    });
    if (bySlug?.sys?.id) return bySlug.sys.id;
  }

  return blogSummaries[0]?.sys?.id ?? null;
}
